import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { Chart, ChartConfiguration, registerables } from 'chart.js';
import { filter } from 'rxjs';
import { GlobalServiceService } from 'src/app/services/global/global-service.service';
Chart.register(...registerables);

interface BaseChartData {
  labels: string[];
  indexAxis: 'x' | 'y';
  type: 'bar' | 'line';
}

interface ChangeChartData extends BaseChartData {
  change: true;
  data: number[];
  dataP: number[];
  dataN: number[];
}

interface AlertsChartData extends BaseChartData {
  alerts: true;
  label: string;
  data: number[];
}

interface ScoreChartData extends BaseChartData {
  score: true;
  data0: number[];
  data1: number[];
  data2: number[];
  data3: number[];
  data4: number[];
  data5: number[];
  data6: number[];
}

interface SimpleChartData extends BaseChartData {
  data: number[];
  label: string;
}

type ChartInputData =
  | ChangeChartData
  | AlertsChartData
  | ScoreChartData
  | SimpleChartData;

const CHART_DEFAULTS = {
  BAR_THICKNESS: 30,
  MIN_HEIGHT: 300,
  PADDING: 50,
  DEFAULT_HEIGHT: '300px',
};

Chart.register(...registerables);

@Component({
    selector: 'app-charts-all-ind-cl',
    templateUrl: './charts-all-ind-cl.component.html',
    styleUrls: ['./charts-all-ind-cl.component.scss'],
    standalone: false
})
export class ChartsAllIndClComponent implements OnInit, OnDestroy {
  chartInstance: Chart;
  private chartConfig: ChartConfiguration;

  private subscription: any;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    public readonly globalServiceService: GlobalServiceService
  ) {}

  ngOnInit(): void {
    this.subscription = this.globalServiceService.chartAll$
      .pipe(filter((data): data is ChartInputData => !!data))
      .subscribe((data: ChartInputData) => this.processChartData(data));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.chartInstance?.destroy();
  }

  private processChartData(data: ChartInputData): void {
    this.updateContainerHeight(data);

    this.chartConfig = this.buildChartConfig(data);

    this.renderChart();
  }

  private buildChartConfig(data: ChartInputData): ChartConfiguration {
    const config: ChartConfiguration = {
      type: data.type,
      data: {
        labels: data.labels,
        datasets: [],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: data.indexAxis,
      },
    };

    if ('change' in data) {
      config.data.datasets = this.buildChangeDatasets(data);

      config.options.scales = {
        x: { stacked: true, beginAtZero: true },
        y: { stacked: true },
      };
    } else if ('alerts' in data) {
      config.data.datasets = this.buildAlertsDatasets(data);
      config.options.scales = {
        x: { beginAtZero: false },
        y: {},
      };
    } else if ('score' in data) {
      config.data.datasets = this.buildScoreDatasets(data);
      config.options.scales = {
        x: { stacked: true, beginAtZero: true },
        y: { stacked: true },
      };
    } else {
      config.data.datasets = this.buildSimpleDatasets(data);
      config.options.scales = {
        x: { stacked: true, beginAtZero: true },
        y: { stacked: true },
      };
    }

    return config;
  }

  private buildChangeDatasets(data: ChangeChartData) {
    if (data.type === 'bar') {
      return [
        {
          label: $localize`Sin variación`,
          data: data.data,
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
        },
        {
          label: $localize`Variación positiva`,
          data: data.dataP,
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
        },
        {
          label: $localize`Variación negativa`,
          data: data.dataN,
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
        },
      ];
    }

    return [
      {
        axis: 'y',
        label: $localize`Sin variación`,
        data: data.data,
        fill: false,
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
        borderColor: 'rgba(54, 162, 235)',
        borderWidth: 1,
      },
      {
        axis: 'y',
        label: $localize`Variación positiva`,
        data: data.dataP,
        fill: false,
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        borderColor: 'rgba(75, 192, 192)',
        borderWidth: 1,
      },
      {
        axis: 'y',
        label: $localize`Variación negativa`,
        data: data.dataN,
        fill: false,
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        borderColor: 'rgba(255, 99, 132)',
        borderWidth: 1,
      },
    ];
  }

  private buildAlertsDatasets(data: AlertsChartData) {
    return [
      {
        label: data.label,
        data: data.data,
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
      },
    ];
  }

  private buildScoreDatasets(data: ScoreChartData) {
    return [
      { label: '1 - 31', data: data.data0, backgroundColor: '#ffa8b8' },
      { label: '32 - 117', data: data.data1, backgroundColor: '#ffc8c3' },
      { label: '118 - 231', data: data.data2, backgroundColor: '#f7d8c3' },
      { label: '232 - 441', data: data.data3, backgroundColor: '#fae187' },
      { label: '442 - 607', data: data.data4, backgroundColor: '#c5d084' },
      { label: '608 - 795', data: data.data5, backgroundColor: '#a0d995' },
      { label: '796 - 999', data: data.data6, backgroundColor: '#a2edce' },
    ];
  }

  private buildSimpleDatasets(data: SimpleChartData) {
    return [
      {
        label: data.label,
        data: data.data,
        backgroundColor: 'rgba(54, 162, 235, 0.5)',
      },
    ];
  }

  private updateContainerHeight(data: ChartInputData): void {
    const canvas =
      this.elementRef.nativeElement.querySelector<HTMLCanvasElement>(
        '#chartsAll'
      );
    if (!canvas?.parentElement) return;

    let height = CHART_DEFAULTS.DEFAULT_HEIGHT;

    if (data.type === 'bar' && data.labels.length > 0) {
      const { BAR_THICKNESS, MIN_HEIGHT, PADDING } = CHART_DEFAULTS;
      const calculatedHeight = data.labels.length * BAR_THICKNESS + PADDING;
      console.log('Calculated height:', calculatedHeight);
      height = `${Math.max(calculatedHeight, MIN_HEIGHT)}px`;
    }

    canvas.parentElement.style.height = height;
  }

  private renderChart(): void {
    this.chartInstance?.destroy();

    const canvas =
      this.elementRef.nativeElement.querySelector<HTMLCanvasElement>(
        '#chartsAll'
      );
    if (canvas) {
      this.chartInstance = new Chart(canvas, this.chartConfig);
    }
  }
}
